/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton, Stack, Tooltip } from '@mui/material';

import { KubernetesIdentifier } from 'shared';

import { EditableTypography } from '../../pages/ManageOrganizationPage/components/TeamsTab/EditableTypography';
import { useDescriptionPanelStyles } from '../../styles/descriptionPanel';
import { DescriptionPanelLoader } from './DescriptionPanelLoader';

type Props = {
  name: string;
  description: string | null;
  icon: React.ReactElement<any>;
  isLoading: boolean;
  isDeletable: boolean;
  isEditable: boolean;
  handleDelete: () => void;
  handleManage: () => void;
  onSubmit: (value: Partial<{ name: string; description: string | null }>) => void;
};

export const DescriptionPanel = memo(
  ({ name, description, handleDelete, icon, isLoading, onSubmit, handleManage, isEditable, isDeletable }: Props) => {
    const classes = useDescriptionPanelStyles();
    const { t } = useTranslation();

    return (
      <Stack direction="row" justifyContent="space-between" className={classes.panel}>
        {isLoading ? (
          <DescriptionPanelLoader />
        ) : (
          <>
            <Stack direction="row" width="100%">
              {icon && <div className={classes.panelIconWrapper}>{icon}</div>}
              <Stack direction="column" gap={1} width="100%">
                <EditableTypography
                  className={classes.panelTitle}
                  readOnly={!isEditable}
                  value={name}
                  schema={Yup.string()
                    .trim()
                    .min(
                      KubernetesIdentifier.MinLength,
                      t('validation:rules.min_length', {
                        field: t(`general:description_panel.form.name`),
                        minLength: KubernetesIdentifier.MinLength,
                      }),
                    )
                    .max(
                      KubernetesIdentifier.MaxLength,
                      t('validation:rules.max_length', {
                        field: t(`general:description_panel.form.name`),
                        maxLength: KubernetesIdentifier.MaxLength,
                      }),
                    )
                    .matches(KubernetesIdentifier.Regex, t('validation:rules.invalid'))
                    .required(t('validation:rules.empty', { field: t(`general:description_panel.form.name`) }))}
                  onSubmit={async value => {
                    if (value) {
                      onSubmit({ name: value! });
                    }
                  }}
                  data-cy="description-panel-name-input"
                />
                <EditableTypography
                  readOnly={!isEditable}
                  value={description}
                  placeholder={t(`general:description_panel.form.description_placeholder`)}
                  schema={Yup.string()
                    .trim()
                    .nullable()
                    .max(
                      200,
                      t('validation:rules.max_length', {
                        field: t(`general:description_panel.form.description`),
                        maxLength: 200,
                      }),
                    )}
                  onSubmit={async value => onSubmit({ description: value })}
                  data-cy="description-panel-description-input"
                />
              </Stack>
            </Stack>

            <div className={classes.panelEditWrapper}>
              <Tooltip title={t(`general:description_panel.control_icons.manage`)!}>
                <IconButton
                  sx={{ padding: 0 }}
                  onClick={handleManage}
                  size="small"
                  data-cy="description-panel-management-button"
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
              {isDeletable && (
                <Tooltip title={t(`general:description_panel.control_icons.delete`)!}>
                  <IconButton
                    sx={{ padding: 0 }}
                    onClick={handleDelete}
                    size="small"
                    data-cy="description-panel-delete-button"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </>
        )}
      </Stack>
    );
  },
);
